/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl} from '@angular/forms';

import {finalize, map, takeUntil, tap} from 'rxjs/operators';

import find from 'lodash-es/find';

import {responseCallbackToTreeNodes} from '../../+organizations/org-tree.service';
import {Destroyable} from '../../destroyable.component';
import {ApiService} from '../../shared/api';
import {FieldType} from '../../shared/form-generator/fields/fields.interfaces';
import {GPTranslateService} from '../../shared/localization/translate.service';
import {TreeNode} from '../../shared/ui';
import {ElasticQueryService} from '../elasticquery.service';
import {isNumberType, isTranslatableType} from '../preview';
import {Report} from '../report';
import {COMMON_SIGNS, EMPTY_SIGNS_WITH_SYMBOL, NUMBER_SIGNS, STRING_SIGNS} from '../reports.constants';
import {Filter, FilterField, FilterItem, FilterSign, Sign} from '../reports.interfaces';
import {ReportsService} from '../reports.service';

const isNotEmpty = (value: string): boolean => value !== undefined && value !== '';

@Component({
  selector: 'gp-filter-item',
  templateUrl: './filter-item.component.html',
  styleUrls: ['./filter-item.component.scss']
})

export class FilterItemComponent extends Destroyable implements OnChanges, AfterViewInit {
  @ViewChild('filter', {static: true}) public filter: ElementRef;
  @Input() public report: Report;
  @Input() public config: FilterField;
  @Input() public enabledFields: string[];
  @Input() public isOpen: boolean = false;
  @Input() public toggleable = true;
  @Input() public draggable = false;
  @Input() public width: number;
  @Output() public removeFilterItem: EventEmitter<string> = new EventEmitter<string>();
  @Output() public filterItemLoaded: EventEmitter<void> = new EventEmitter<void>();
  @Output() public dragHandleActive: EventEmitter<boolean> = new EventEmitter<boolean>();

  public items: FilterItem[] | Filter[] = [];
  public conditionalSigns: Sign[] = [];
  public payValueType = FieldType.PAY_VALUE;

  public newCondition: Filter = {
    sign: FilterSign.EQ,
    value: '',
    display: ''
  };

  public selectedConditionFormControl: UntypedFormControl = this.formBuilder.control('');

  constructor(private formBuilder: UntypedFormBuilder,
              private api: ApiService,
              private cd: ChangeDetectorRef,
              private reportsService: ReportsService,
              private gpTranslateService: GPTranslateService,
              private elasticQueryService: ElasticQueryService) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.config.metadatas.disabled = !this.isFieldEnabled(this.config);

    switch (this.config.fieldType) {
      case FieldType.DATE:
      case FieldType.INTEGER:
      case FieldType.DECIMAL:
      case FieldType.CURRENCY:
      case FieldType.PERCENTAGE:
      case FieldType.ABSENCE:
      case FieldType.PAY_VALUE:
        this.conditionalSigns = [...COMMON_SIGNS, ...EMPTY_SIGNS_WITH_SYMBOL, ...NUMBER_SIGNS];
        break;

      case FieldType.LABEL:
        this.conditionalSigns = [...COMMON_SIGNS, ...EMPTY_SIGNS_WITH_SYMBOL, ...STRING_SIGNS];
        break;

      case FieldType.TREE:
      case FieldType.LIST:
        this.conditionalSigns = [...COMMON_SIGNS, ...EMPTY_SIGNS_WITH_SYMBOL];
        break;

      case FieldType.PEOPLE_TREE:
      case FieldType.ENTITY:
      case FieldType.ENTITY_DATA:
      case FieldType.ENTITY_PREFIXED:
      default: // FILE, FILES and BOOLEAN types
        this.conditionalSigns = [...COMMON_SIGNS, ...EMPTY_SIGNS_WITH_SYMBOL];
        break;
    }

    if (this.config.field === 'empContract.status') {
      this.config.fieldType = FieldType.LABEL;
    }
    switch (this.config.fieldType) {
      case FieldType.LIST:
        this.initListFieldWithAutoComplete();
        break;
      case FieldType.TREE:
        this.initListFieldWithMetaData();
        break;
      case FieldType.LABEL:
        this.initLabelField();
        break;
      case FieldType.ENTITY:
      case FieldType.ENTITY_DATA:
      case FieldType.ENTITY_PREFIXED:
        this.initEntityField();
        break;
      case FieldType.BOOLEAN:
        this.initBooleanField();
        break;
      case FieldType.FILE:
      case FieldType.FILES:
        break;
    }
  }

  public shouldShowNgxSelect(newConditionSign: string): boolean {
    return !!this.items?.length && STRING_SIGNS.filter(sign => sign.value === newConditionSign).length === 0;
  }

  public isNumberType(): boolean {
    return isNumberType(this.config.fieldType);
  }

  public isTranslatableType(): boolean {
    return isTranslatableType(this.config.fieldType);
  }

  public getSortValue(a: any, b: any): number {
    if (a.display > b.display) {
      return 1;
    }

    if (a.display < b.display) {
      return -1;
    }
  }

  public ngAfterViewInit(): void {
    this.filterItemLoaded.emit();
  }

  // ----------------------------------------------Main------------------------------------------------------------------
  public removeFilter(): void {
    this.removeFilterItem.next(this.config.field);
  }

  // ----------------------------------------------Filter list-----------------------------------------------------------
  public removeValue(condition: any): void {
    const index = this.config.filter.indexOf(condition);
    if (index !== -1) {
      this.config.filter.splice(index, 1);
      this.reportsService.$reportUpdated.next(this.report);
      this.reportsService.markAsChanged();
    }
  }

  public getConditionDisplay(value: string): string {
    const sign = this.conditionalSigns.find((element) => element.value === value);
    return sign ? sign?.display : '';
  }

  public getConditionText(value: string): string {
    const sign = this.conditionalSigns.find((element) => element.value === value);
    const signText = sign ? sign?.text : '';
    return this.gpTranslateService.instant(signText);
  }

  public handleEnterKeyPress(event): void {
    if (event.key === 'Enter' && this.isValidFilterValue()) {
      this.addFilterValue();
      this.cd.detectChanges();
    }
  }

  public addFilterValue(): void {
    this.addFilterToConfig();
    this.resetNewCondition();
    this.reportsService.$reportUpdated.next(this.report);
    this.reportsService.markAsChanged();
  }

  public addEmptyNotEmptyFilterValue() {
    if (this.newCondition.sign === FilterSign.EM || this.newCondition.sign === FilterSign.NEM) {
      this.config.filter.push({...this.newCondition});
      this.resetToEQSignIfEmptySign();
    }
  }

  public getTranslatedSignText(signText: string): string {
    return this.gpTranslateService.instant(signText);
  }

  public computeWidth(): any {
    return {
      'max-width': `${this.filter.nativeElement.clientWidth - 120}px`,
      'width': '100%'
    };
  }

  private resetNewCondition() {
    this.selectedConditionFormControl.setValue(null);
    this.newCondition = {
      sign: FilterSign.EQ,
      value: '',
      display: ''
    };
  }

  private addFilterToConfig() {
    const selectCondition = this.findSelectCondition();
    if (selectCondition?.value) {
      this.newCondition = {
        ...selectCondition,
        value: selectCondition.value,
        sign: this.newCondition.sign
      };
    } else {
      this.newCondition.display = this.newCondition.value;
    }
    this.config.filter.push({...this.newCondition});
  }

  private findSelectCondition(): Filter {
    if (!this.items.length) {
      return null;
    }
    const value = this.selectedConditionFormControl.value;
    const item = find(<Filter[] | FilterItem[]>this.items,
      (filter: Filter | FilterItem) => filter.value === value);

    return {
      value,
      display: item ? item.display : value,
      sign: null
    };
  }

  public isValidFilterValue(): boolean {
    return this.newCondition.sign &&
      (isNotEmpty(this.newCondition.value) || !!this.selectedConditionFormControl.value);
  }

  public removeSelectedCondition() {
    this.selectedConditionFormControl.setValue(null);
  }

  // ----------------------------------------------Entity field----------------------------------------------------------
  public initEntityField(): void {
    this.api.get(this.config.metadatas.rel)
      .pipe(finalize(() => this.cd.markForCheck()),
        map((response: any) => response.map((element: any) => {
          return <FilterItem>{
            display: element.name,
            parentValue: null,
            selectable: false,
            value: element.id,
            sign: FilterSign.EQ
          };
        })),
        tap((items: FilterItem[]) => {
          this.items = items.sort(this.getSortValue);
        }),
        takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public entityResponseCallback(response: any, lookupMap: Map<any, any>, isDefaultRecipient?: boolean): TreeNode<any>[] {
    return responseCallbackToTreeNodes(response, lookupMap, isDefaultRecipient);
  }

  // ----------------------------------------------List field from metadata------------------------------------------------------------
  public initListFieldWithMetaData(): void {
    this.items = this.config.metadatas.items
      .map((value: any) => {
        value.sign = FilterSign.EQ;
        return value;
      })
      .sort((a: any, b: any) => this.getSortValue(a, b));
  }

  // ----------------------------------------------List field from Elastic search-----------------------------------------------------------
  public initListFieldWithAutoComplete(): void {
    this.elasticQueryService.getAutoCompleteItemsWithTransIds(this.config.filterDocument, this.config.field, this.config.fieldType)
      .pipe(
        finalize(() => this.cd.markForCheck()),
        map((response: { [key: string]: string }) => {
          return Object.entries(response)
            .map(([value, transId]) => ({value, display: transId, sign: FilterSign.EQ}))
            .sort((a: any, b: any) => this.getSortValue(a, b)) as Filter[];
        }),
        tap((items: Filter[]) => {
          this.items = items;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }


  // ----------------------------------------------Boolean field---------------------------------------------------------
  public initBooleanField(): void {
    this.items = [{
      display: 'True',
      parentValue: null,
      selectable: true,
      value: 'true'
    }, {
      display: 'False',
      parentValue: null,
      selectable: true,
      value: 'false'
    }];
  }

  // ----------------------------------------------Label field-----------------------------------------------------------
  public initLabelField(): void {
    this.elasticQueryService.getAutoCompleteItems(this.config.filterDocument, this.config.field, this.config.fieldType)
      .pipe(
        finalize(() => this.cd.markForCheck()),
        map((response: string[]) => {
          return response
            .map((value: string) => ({value: value, display: value, sign: FilterSign.EQ}))
            .sort((a: any, b: any) => this.getSortValue(a, b)) as Filter[];
        }),
        tap((items: Filter[]) => {
          this.items = items;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  // --------------------------------------------------------------------------------------------------------------------

  public transIdsDisplay(transIds: string[]): string {
    return this.gpTranslateService.getDisplayTranslation(transIds, ' | ');
  }

  public isFieldEnabled(item: FilterField) {
    return this.enabledFields && this.enabledFields.length && this.enabledFields.includes(item.field);
  }

  public toggleFilterList() {
    if (!this.config.metadatas.disabled && this.toggleable) {
      this.isOpen = !this.isOpen;
    }
  }

  private resetToEQSignIfEmptySign(): void {
    this.newCondition.sign = null;
    this.cd.detectChanges();
    this.newCondition.sign = FilterSign.EQ;
  }
}
